import { useState } from 'react';
import { DollarSign, Send, RefreshCw, Store } from 'lucide-react';
import { MarketplacePrices as MarketplacePricesType } from '../services/baselinker';
import PriceUpdateRequestModal from './PriceUpdateRequestModal';

interface MarketplacePricesProps {
  marketplacePrices: {
    emtuacasa: number;
    worten: number;
    fnac: number;
    amazon: number;
  };
  productEan: string;
  productName: string;
  companyName: string;
  baselinker_id: string;
  product: {
    regularPrice: number;
    salePrice?: number;
    promotionalPeriod?: {
      startDate: string;
      endDate: string;
    };
    isPrivateSale?: boolean;
  };
  companyLogo?: string;
}

const COMMISSION_RATE = 0.15; // 15%
const VAT_RATE = 0.23; // 23%

export default function MarketplacePrices({ 
  marketplacePrices, 
  productEan, 
  productName, 
  companyName,
  baselinker_id,
  product,
  companyLogo
}: MarketplacePricesProps) {
  const [showPricesModal, setShowPricesModal] = useState(false);

  const isPromotionalPriceActive = (): boolean => {
    if (!product.promotionalPeriod?.startDate || !product.promotionalPeriod?.endDate || !product.salePrice) {
      return false;
    }

    const now = new Date().getTime();
    const startDate = new Date(product.promotionalPeriod.startDate).getTime();
    const endDate = new Date(product.promotionalPeriod.endDate).getTime();

    return now >= startDate && now <= endDate;
  };

  const getCurrentPrice = (): number => {
    return isPromotionalPriceActive() ? (product.salePrice || product.regularPrice) : product.regularPrice;
  };

  const calculateSellerReceives = (price: number, isPrivateSale: boolean) => {
    // Para vendas no site próprio, não há comissão
    if (isPrivateSale) {
      return price;
    }

    // Calcular comissão (15%)
    const commission = price * COMMISSION_RATE;
    
    // Calcular IVA sobre a comissão (23%)
    const vatOnCommission = commission * VAT_RATE;
    
    // Calcular total de descontos
    const totalDeductions = commission + vatOnCommission;
    
    // Calcular valor que o vendedor recebe
    return price - totalDeductions;
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    });
  };

  // Transform the prices map into marketplace format
  const marketplaces = [
    { 
      id: 'emtuacasa', 
      name: 'NO SEU SITE', 
      price: marketplacePrices.emtuacasa || 0,
      logo: companyLogo || '',
      bgColor: 'bg-gradient-to-br from-primary to-primary-dark',
      isPrivateSale: product.isPrivateSale
    },
    { 
      id: 'worten', 
      name: 'Worten', 
      price: marketplacePrices.worten || 0,
      logo: 'https://logy.shippix.pt/worten.png',
      bgColor: 'bg-[#ff0000]'
    },
    { 
      id: 'fnac', 
      name: 'Fnac', 
      price: marketplacePrices.fnac || 0,
      logo: 'https://logy.shippix.pt/fnac.png',
      bgColor: 'bg-[#0066c0]'
    },
    { 
      id: 'amazon', 
      name: 'Amazon', 
      price: marketplacePrices.amazon || 0,
      logo: 'https://logy.shippix.pt/amazon.png',
      bgColor: 'bg-[#232f3e]'
    },
    {
      id: 'kuantokusta',
      name: 'KuantoKusta',
      price: getCurrentPrice(),
      logo: 'https://logy.shippix.pt/kk.png',
      bgColor: 'bg-[#00a6ce]'
    }
  ];

  const handleRefreshPrices = () => {
    // Implementar lógica para atualizar preços
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Preços nos Marketplaces</h3>
        {/* Botão temporariamente oculto
        <button 
          onClick={() => setShowPricesModal(true)}
          className="text-yellow-500 hover:text-yellow-600 flex items-center gap-1 text-sm"
        >
          <RefreshCw size={14} />
          Atualizar preços
        </button>
        */}
      </div>

      <div className="space-y-2">
        {marketplaces.map((marketplace) => (
          <div key={marketplace.id} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded ${
                  marketplace.id === 'emtuacasa' ? 'bg-gray-800' : 'bg-gray-800'
                }`}>
                  {marketplace.id === 'emtuacasa' ? (
                    <Store className="h-5 w-5 text-white" />
                  ) : (
                    <img 
                      src={marketplace.logo}
                      alt={marketplace.name}
                      className="h-6 w-6 object-contain brightness-0 invert"
                    />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{marketplace.name}</h4>
                  {marketplace.id === 'emtuacasa' ? (
                    <span className="text-xs text-green-600">
                      Isento de comissões
                    </span>
                  ) : (
                    <span className="text-xs text-gray-600">
                      Comissão 15% + IVA
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-medium text-gray-900">
                  {formatPrice(marketplace.price)}
                </div>
                <div className="text-sm text-gray-500">
                  Valor a receber: {formatPrice(calculateSellerReceives(marketplace.price, marketplace.id === 'emtuacasa'))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <PriceUpdateRequestModal
        isOpen={showPricesModal}
        onClose={() => setShowPricesModal(false)}
        marketplaces={marketplaces}
        productEan={productEan}
        productName={productName}
        companyName={companyName}
        baselinker_id={baselinker_id}
      />
    </div>
  );
}